import { useState, useEffect, useCallback, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Banner,
  Card,
  Text,
  InlineStack,
  BlockStack
} from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import YearMonthSelect from "components/year-month-select/YearMonthSelect";
import AssetsSelect from "components/assets-select/AssetsSelect";
import FuelCardSelect from "components/fuel-card-select/FuelCardSelect";
import {
  createFuelReport,
  fetchFuelReportById,
  updateFuelReport,
} from "services/fuel-report.service";
import { fetchFuelReportsThunk } from "containers/user-assets-fuel-reports/fuel-reports.slice";
import {
  fetchFuelReportsForCard,
  fetchLatestFuelReports,
} from "services/fuel-report.service";
import { AppDispatch, RootState } from "configureStore";
import DataTable from "components/datatable/Datatable";
import { fetchFuelReportDetails } from "containers/fuel-report-view-container/fuel-report-container.slice";
import { Column } from "types/Datatable";
import { ManualFuelExpense } from "types/FuelExpense";
import { FuelReport } from "types/FuelReport";
import { Asset } from "types/Asset";
import FuelReportFormSkeleton from "./fuel-report-form-skeleton";
import {
  calculateWorkPurposeKmFromRate,
  getMonthMileage,
  getNonWorkPurposeKm,
  getSeasonalConsumptionRate,
  isFullElectricVehicle,
} from "utils/fuelReportUtils";
import { Select, DropZone, Link } from "@shopify/polaris";
import { setDestroy, setIsModalxOpen, setModalxOptions } from "components/modalx/modalx.slice";
import {
  ManualExpenseFormRow,
} from "components/manual-expense-row/ManualExpenseRow";
import DatePicker from "features/datepicker/date-picker";
import { upload } from "services/upload-file.service.js";
import { EditIcon, DeleteIcon } from "@shopify/polaris-icons";
import { formatPrettyDate, escapeHtml } from "utils/helperFunctions";
import { fuelReportErrorMessage } from "utils/fuel-report-errors";
import useIsPhone from "features/files/use-is-phone";
import { setTypedDecimal, typedDecimal } from "utils/decimal-input";
import { API_URL } from "../../constants";

interface AddFuelReportFormProps {
  id?: string;
  source?: string;
  /** A new report opens for this month (YYYY-MM) instead of the current one. */
  period?: string;
  /** A new report opens with this vehicle picked, if the vehicle list holds it. */
  assetId?: string;
}

const AddFuelReportForm: React.FC<AddFuelReportFormProps> = ({
  id,
  source = "",
  period: preselectedPeriod,
  assetId: preselectedAssetId,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const isPhone = useIsPhone();
  const { role } = useSelector((state: RootState) => state.authReducer);
  const { fuelReportEntity } = useSelector(
    (state: RootState) => state.fuelReportContainerReducer
  );

  const [yearMonth, setYearMonth] = useState<string>(() => {
    if (preselectedPeriod) return preselectedPeriod;
    const now = new Date();
    return `${now.getFullYear()}-${("0" + (now.getMonth() + 1)).slice(-2)}`;
  });
  const [selectedAsset, setSelectedAsset] = useState<string>("");
  const [selectedAssetFuelType, setSelectedAssetFuelType] = useState<string>("");
  const [selectedWinterRate, setSelectedWinterRate] = useState<string>("");
  const [selectedSummerRate, setSelectedSummerRate] = useState<string>("");
  const [workPurposeKm, setWorkPurposeKm] = useState<string>("");
  const [workPurposeKmTouched, setWorkPurposeKmTouched] = useState(false);
  const [selectedFuelCard, setSelectedFuelCard] = useState<string[]>([]);
  const [endOfMonthKm, setEndOfMonthKm] = useState<string>("");
  const [endOfMonthLtr, setEndOfMonthLtr] = useState<string>("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  // Display-ready text (already translated) — never run it through `t()` again.
  const [errMsg, setErrMsg] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Saved: the modal is only waiting to close, so Save stays off.
  const [isSaved, setIsSaved] = useState<boolean>(false);
  // A ref, not state: two fast clicks land before a re-render, and both would
  // otherwise pass the check and send the report twice.
  const submitInFlightRef = useRef(false);
  // The saved form closes its modal after a short while; a form that is gone by then must not
  // close the modal of the form that came after it. Another report, month or vehicle never reuses
  // this form: `FuelReportForm` below keys it by them, so the old one unmounts and clears its timer.
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(closeTimerRef.current), []);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(!!id);
  const [reportData, setReportData] = useState<any>(null);
  const [additionalExpensesData, setAdditionalExpensesData] =
    useState<any>(null);
  // Array of all manual expenses (both existing and new) as form rows
  const [manualExpenseFormRows, setManualExpenseFormRows] = useState<
    ManualExpenseFormRow[]
  >([]);
  
  // State for the form (null = hidden, "new" = adding new, expenseId = editing)
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  
  // Form state for adding/editing
  const [formDate, setFormDate] = useState<string>("");
  const [formExpenseType, setFormExpenseType] = useState<"fuel" | "other">("fuel");
  const [formAmount, setFormAmount] = useState<string>("");
  const [formQuantity, setFormQuantity] = useState<string>("");
  const [formDescription, setFormDescription] = useState<string>("");
  const [formFile, setFormFile] = useState<string | { id: string; name?: string; title?: string } | undefined>(undefined);
  const [formMeasurementUnit, setFormMeasurementUnit] = useState<string>("");
  const [formFileUploading, setFormFileUploading] = useState<boolean>(false);
  const [formFileDialogOpen, setFormFileDialogOpen] = useState<boolean>(false);
  const [startOfMonthKm, setStartOfMonthKm] = useState<string>("");
  const [startOfMonthLtr, setStartOfMonthLtr] = useState<string>("");
  const [startKmLocked, setStartKmLocked] = useState(false);
  const [startLtrLocked, setStartLtrLocked] = useState(false);
  const [evChargeKw, setEvChargeKw] = useState<string>("");
  const [evChargeAmount, setEvChargeAmount] = useState<string>("");

  const isEditMode = !!id;

  // Every opener of this form (the report page, the cabinet's months) gets the sheet's title
  // from here, so a phone sheet shows what it is for, not only a close button.
  useLayoutEffect(() => {
    const title = isEditMode
      ? t("edit_fuel_report", { defaultValue: "Update fuel report" })
      : t("create_fuel_report", { defaultValue: "Fill in fuel report" });
    dispatch(setModalxOptions({ title }));
  }, [dispatch, isEditMode, t]);

  const appliedReportIdRef = useRef<string | null>(null);
  const isElectricVehicle = isFullElectricVehicle(selectedAssetFuelType);
  const monthMileage = getMonthMileage(startOfMonthKm, endOfMonthKm);
  const electricRate = isElectricVehicle
    ? getSeasonalConsumptionRate(
        selectedWinterRate,
        selectedSummerRate,
        yearMonth
      )
    : 0;
  const calculatedWorkPurposeKm = isElectricVehicle
    ? calculateWorkPurposeKmFromRate(evChargeKw, electricRate)
    : 0;
  const nonWorkPurposeKm = isElectricVehicle
    ? getNonWorkPurposeKm(monthMileage, workPurposeKm)
    : 0;

  const applyReportDataToForm = useCallback((data: FuelReport) => {
    const cardIds = Array.isArray(data?.fuelCards)
      ? data.fuelCards
          .map((entity) => (typeof entity === "object" ? entity.id : entity))
          .filter(Boolean)
      : [];

    setYearMonth(data?.period || "");

    const assetId = data?.asset
      ? typeof data.asset === "object"
        ? data.asset.id
        : data.asset
      : "";

    setSelectedAsset(assetId || "");
    setSelectedAssetFuelType(
      data?.asset && typeof data.asset === "object"
        ? data.asset.fuelType || ""
        : ""
    );
    if (data?.asset && typeof data.asset === "object") {
      setSelectedWinterRate(
        data.asset.winterPeriodFuelRate != null
          ? String(data.asset.winterPeriodFuelRate)
          : ""
      );
      setSelectedSummerRate(
        data.asset.summerPeriodFuelRate != null
          ? String(data.asset.summerPeriodFuelRate)
          : ""
      );
    }

    setSelectedFuelCard(cardIds);
    setEndOfMonthKm(data?.endOfMonthKm != null ? String(data.endOfMonthKm) : "");
    setEndOfMonthLtr(
      data?.endOfMonthLtr != null ? String(data.endOfMonthLtr) : ""
    );
    setEvChargeKw(data?.evChargeKw != null ? String(data.evChargeKw) : "");
    setEvChargeAmount(
      data?.evChargeAmount != null ? String(data.evChargeAmount) : ""
    );
    setStartOfMonthKm(
      data?.startOfMonthKm != null ? String(data.startOfMonthKm) : ""
    );
    setStartOfMonthLtr(
      data?.startOfMonthLtr != null ? String(data.startOfMonthLtr) : ""
    );
    setWorkPurposeKm(
      data?.workPurposeKm !== null && data?.workPurposeKm !== undefined
        ? String(data.workPurposeKm)
        : ""
    );
    setWorkPurposeKmTouched(
      data?.workPurposeKm !== null && data?.workPurposeKm !== undefined
    );
    setStartKmLocked(false);
    setStartLtrLocked(false);

    setReportData(
      Array.isArray(data?.fuelRecords) ? data.fuelRecords : []
    );
    setAdditionalExpensesData(
      Array.isArray(data?.additionalExpensesRecords)
        ? data.additionalExpensesRecords
        : []
    );

    if (data?.manualExpenses && Array.isArray(data.manualExpenses)) {
      const formRows: ManualExpenseFormRow[] = data.manualExpenses.map(
        (exp: ManualFuelExpense) => {
          let fileValue:
            | string
            | { id: string; name?: string; title?: string }
            | undefined = undefined;
          if (exp.file) {
            if (typeof exp.file === "object" && exp.file?.id) {
              fileValue = {
                id: exp.file.id,
                name: exp.file.name,
                title: exp.file.title,
              };
            } else if (typeof exp.file === "string") {
              fileValue = exp.file;
            }
          }

          return {
            id: exp.id || `existing-${Date.now()}-${Math.random()}`,
            date: exp.date || "",
            expenseType: exp.expenseType || "fuel",
            amount: String(exp.amount || ""),
            quantity: exp.quantity ? String(exp.quantity) : "",
            description: exp.description || "",
            file: fileValue,
            measurementUnit: exp.measurementUnit || undefined,
            createdAt: exp.createdAt || new Date().toISOString(),
          };
        }
      );
      setManualExpenseFormRows(formRows);
    } else {
      setManualExpenseFormRows([]);
    }
  }, []);

  useLayoutEffect(() => {
    if (!isEditMode || !companyId || !id) {
      return;
    }

    if (appliedReportIdRef.current === id) {
      return;
    }

    if (fuelReportEntity?.id === id) {
      applyReportDataToForm(fuelReportEntity);
      setIsInitialLoading(false);
      appliedReportIdRef.current = id;
    }
  }, [isEditMode, companyId, id, fuelReportEntity, applyReportDataToForm]);

  useEffect(() => {
    if (!isEditMode || !companyId || !id) {
      return;
    }

    if (appliedReportIdRef.current === id) {
      return;
    }

    let cancelled = false;

    (async () => {
      setIsInitialLoading(true);
      try {
        const data = await fetchFuelReportById(id, companyId);
        if (cancelled || appliedReportIdRef.current === id) {
          return;
        }
        applyReportDataToForm(data);
        appliedReportIdRef.current = id;
      } catch (e) {
        if (!cancelled) {
          console.error("Error loading fuel report:", e);
          setErrMsg(t("error_loading_report"));
        }
      } finally {
        if (!cancelled) {
          setIsInitialLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEditMode, companyId, id, applyReportDataToForm, t]);

  useEffect(() => {
    if (isEditMode || !selectedAsset || !yearMonth || !companyId) {
      return;
    }
    fetchPreviousReport(selectedAsset, yearMonth);
  }, [selectedAsset, companyId, yearMonth, id, isEditMode]);

  useEffect(() => {
    if (!isElectricVehicle || workPurposeKmTouched) {
      return;
    }
    setWorkPurposeKm(
      calculatedWorkPurposeKm > 0 ? String(calculatedWorkPurposeKm) : ""
    );
  }, [
    isElectricVehicle,
    workPurposeKmTouched,
    calculatedWorkPurposeKm,
  ]);

  // Manual expenses are now loaded from fuel report, no separate fetch needed

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!selectedAsset) newErrors.selectedAsset = t("asset_is_required");
    if (!selectedFuelCard?.length)
      newErrors.selectedFuelCard = t("fuel_card_is_required");
    if (!yearMonth) newErrors.yearMonth = t("year_month_required");
    if (!endOfMonthKm) newErrors.endOfMonthKm = t("end_of_month_km_required");
    if (endOfMonthKm && isNaN(Number(endOfMonthKm)))
      newErrors.endOfMonthKm = t("must_be_number");
    if (!startOfMonthKm)
      newErrors.startOfMonthKm = t("start_of_month_km_required");
    if (startOfMonthKm && isNaN(Number(startOfMonthKm)))
      newErrors.startOfMonthKm = t("must_be_number");

    if (!isElectricVehicle) {
      if (!endOfMonthLtr)
        newErrors.endOfMonthLtr = t("end_of_month_ltr_required");
      if (endOfMonthLtr && isNaN(Number(endOfMonthLtr)))
        newErrors.endOfMonthLtr = t("must_be_number");
      if (!startOfMonthLtr)
        newErrors.startOfMonthLtr = t("start_of_month_ltr_required");
      if (startOfMonthLtr && isNaN(Number(startOfMonthLtr)))
        newErrors.startOfMonthLtr = t("must_be_number");
    }

    // The fields accept a lone separator while a number is being typed; it must not pass as a number.
    if (evChargeKw && isNaN(Number(evChargeKw)))
      newErrors.evChargeKw = t("must_be_number");
    if (evChargeAmount && isNaN(Number(evChargeAmount)))
      newErrors.evChargeAmount = t("must_be_number");

    // Ensure end > start
    if (
      startOfMonthKm &&
      endOfMonthKm &&
      !isNaN(Number(startOfMonthKm)) &&
      !isNaN(Number(endOfMonthKm)) &&
      parseFloat(endOfMonthKm) <= parseFloat(startOfMonthKm)
    ) {
      newErrors.endOfMonthKm = t("end_km_must_be_greater_than_start");
    }

    if (isElectricVehicle) {
      if (workPurposeKm === "") {
        newErrors.workPurposeKm = t("work_purpose_km_required");
      } else if (isNaN(Number(workPurposeKm)) || Number(workPurposeKm) < 0) {
        newErrors.workPurposeKm = t("must_be_number");
      } else if (
        startOfMonthKm &&
        endOfMonthKm &&
        !isNaN(Number(startOfMonthKm)) &&
        !isNaN(Number(endOfMonthKm)) &&
        Number(workPurposeKm) > monthMileage
      ) {
        newErrors.workPurposeKm = t("work_purpose_km_exceeds_monthly_mileage");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (submitInFlightRef.current || isSaved) return;
    if (!validateForm()) return;

    // Validate manual expense form rows
    if (manualExpenseFormRows.length > 0) {
      for (const row of manualExpenseFormRows) {
        if (!row.date || !row.amount) {
          setErrMsg(t("field_is_required"));
          return;
        }

        if (isNaN(Number(row.amount)) || Number(row.amount) <= 0) {
          setErrMsg(t("must_be_number"));
          return;
        }

        // Quantity is required for fuel type and must be greater than zero
        if (row.expenseType === "fuel" && (!row.quantity || isNaN(Number(row.quantity)) || Number(row.quantity) <= 0)) {
          setErrMsg(t("field_is_required"));
          return;
        }
      }
    }

    // Prepare manual expenses array for fuel report payload
    const manualExpenses = manualExpenseFormRows.map((row) => {
      const expense: any = {
        expenseType: row.expenseType,
        amount: parseFloat(row.amount),
        quantity: row.expenseType === "fuel" ? parseFloat(row.quantity) : parseFloat(row.quantity || "0"),
        date: row.date,
        description: row.description || undefined,
        // Extract file ID from file object or use string directly
        file: typeof row.file === "object" && row.file?.id 
          ? row.file.id 
          : typeof row.file === "string" 
            ? row.file 
            : undefined,
      };
      // Include measurementUnit only for fuel expenses
      if (row.expenseType === "fuel") {
        expense.measurementUnit = row.measurementUnit || undefined;
      }
      // Include id if it exists (for updates) - exclude if it's a new expense (starts with "new-") or generated ID (starts with "existing-")
      if (row.id && !row.id.startsWith("new-") && !row.id.startsWith("existing-")) {
        expense.id = row.id;
      }
      return expense;
    });

    const formData = {
      company: companyId,
      asset: selectedAsset,
      fuelCard: selectedFuelCard,
      period: yearMonth,
      startOfMonthKm: startOfMonthKm ? parseFloat(startOfMonthKm) : undefined,
      startOfMonthLtr: isElectricVehicle
        ? 0
        : startOfMonthLtr
          ? parseFloat(startOfMonthLtr)
          : undefined,
      endOfMonthKm: endOfMonthKm ? parseFloat(endOfMonthKm) : undefined,
      endOfMonthLtr: isElectricVehicle
        ? 0
        : endOfMonthLtr
          ? parseFloat(endOfMonthLtr)
          : undefined,
      evChargeKw: evChargeKw ? parseFloat(evChargeKw) : null,
      evChargeAmount: evChargeAmount ? parseFloat(evChargeAmount) : null,
      ...(isElectricVehicle
        ? {
            workPurposeKm: workPurposeKm
              ? parseFloat(workPurposeKm)
              : undefined,
          }
        : {}),
      manualExpenses: manualExpenses,
    };
    submitInFlightRef.current = true;
    try {
      setErrMsg("");
      setIsLoading(true);
      if (isEditMode && companyId && id) {
        await updateFuelReport(companyId, id, formData);
      } else if (companyId) {
        await createFuelReport(companyId, formData);
      }

      // Manual expenses are now part of the fuel report, no separate refresh needed

      if (companyId) dispatch(fetchFuelReportsThunk(companyId, {}));
      if (companyId && id) {
        dispatch(fetchFuelReportDetails({ companyId, reportId: id }));
      }
      setIsSaved(true);
      setSuccessMessage("ok");
      closeTimerRef.current = setTimeout(() => {
        dispatch(setIsModalxOpen(false));
        // Without it the sheet's title stays in the store and heads the next sheet.
        dispatch(setDestroy());
      }, 1500);
    } catch (e) {
      // Only a failed save may be tried again; after a saved one the lock
      // stays until the modal closes.
      submitInFlightRef.current = false;
      setErrMsg(fuelReportErrorMessage(e, t));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssetChange = (assetId: string, asset?: Asset) => {
    setSelectedAsset(assetId);
    setSelectedAssetFuelType(asset?.fuelType || "");
    setSelectedWinterRate(
      asset?.winterPeriodFuelRate != null
        ? String(asset.winterPeriodFuelRate)
        : ""
    );
    setSelectedSummerRate(
      asset?.summerPeriodFuelRate != null
        ? String(asset.summerPeriodFuelRate)
        : ""
    );
    if (isFullElectricVehicle(asset?.fuelType)) {
      setStartOfMonthLtr("");
      setEndOfMonthLtr("");
      setStartLtrLocked(false);
      setWorkPurposeKmTouched(false);
    } else {
      setWorkPurposeKm("");
      setWorkPurposeKmTouched(false);
    }
  };

  // The vehicle asked for through `assetId` is picked as a person would pick it, so its fuel
  // type and seasonal rates are applied.
  const handleAssetsLoaded = (assetsById: Record<string, Asset>) => {
    if (preselectedAssetId && assetsById[preselectedAssetId]) {
      handleAssetChange(preselectedAssetId, assetsById[preselectedAssetId]);
    }
  };

  const fetchPreviousReport = async (assetId: string, yearMonth: string) => {
    try {
      const { data: prevReport } = await fetchLatestFuelReports(
        companyId!,
        assetId,
        yearMonth
      );
      const latest = Array.isArray(prevReport) ? prevReport[0] : prevReport;

      const endKm = latest?.endOfMonthKm;
      const endLtr = latest?.endOfMonthLtr;

      const hasValidKm = endKm !== null && endKm !== undefined;
      const hasValidLtr = endLtr !== null && endLtr !== undefined;
      if (latest?.id !== id) {
        if (hasValidKm) setStartOfMonthKm(hasValidKm ? String(endKm) : "");
        if (hasValidKm) setStartKmLocked(hasValidKm);
        if (!isFullElectricVehicle(selectedAssetFuelType)) {
          if (hasValidLtr) setStartOfMonthLtr(hasValidLtr ? String(endLtr) : "");
          if (hasValidLtr) setStartLtrLocked(hasValidLtr);
        } else {
          setStartOfMonthLtr("");
          setStartLtrLocked(false);
        }
      }
    } catch {
      setStartOfMonthKm("");
      setStartOfMonthLtr("");
      setStartKmLocked(false);
      setStartLtrLocked(false);
    }
  };

  const handleFuelCardChange = async (cardIds: string[]) => {
    setSelectedFuelCard(cardIds);
    if (cardIds.length > 0 && yearMonth && companyId) {
      try {
        const data = await fetchFuelReportsForCard(
          companyId,
          cardIds,
          yearMonth
        );

        // Handle new API response structure with both fuelRecords and additionalExpensesRecords
        if (data.fuelRecords) {
          setReportData(
            Array.isArray(data.fuelRecords) ? data.fuelRecords : []
          );
        }

        // Handle additional expenses data
        if (data.additionalExpensesRecords) {
          setAdditionalExpensesData(
            Array.isArray(data.additionalExpensesRecords)
              ? data.additionalExpensesRecords
              : []
          );
        } else {
          setAdditionalExpensesData([]);
        }
      } catch {
        setReportData([]);
        setAdditionalExpensesData([]);
      }
    } else {
      setReportData([]);
      setAdditionalExpensesData([]);
    }
  };

  const handleYearMonthChange = async (val: string) => {
    setYearMonth(val);
    if (selectedFuelCard?.length > 0 && val && companyId) {
      try {
        const data = await fetchFuelReportsForCard(
          companyId,
          selectedFuelCard,
          val
        );

        // Handle new API response structure with both fuelRecords and additionalExpensesRecords
        if (data.fuelRecords) {
          setReportData(
            Array.isArray(data.fuelRecords) ? data.fuelRecords : []
          );
        } else {
          // Fallback for old API structure
          setReportData(Array.isArray(data) ? data : data ? [data] : []);
        }

        // Handle additional expenses data
        if (data.additionalExpensesRecords) {
          setAdditionalExpensesData(
            Array.isArray(data.additionalExpensesRecords)
              ? data.additionalExpensesRecords
              : []
          );
        } else {
          setAdditionalExpensesData([]);
        }
      } catch {
        setReportData([]);
        setAdditionalExpensesData([]);
      }
    } else {
      setReportData([]);
      setAdditionalExpensesData([]);
    }

    // Manual expenses are now part of the fuel report, no separate fetch needed
  };

  const processedReportData = reportData?.map((item: any) => ({
    ...item,
    amount:
      (item.amountWithoutDiscount || 0) -
      (item.discountAmount || 0) -
      (item.receiptFees || 0) +
      (item.discountsOnTaxes || 0),
  }));

  const processedAdditionalExpensesData = additionalExpensesData?.map(
    (item: any, idx: number) => ({
      id: item.id,
      "#": idx + 1,
      dateTime: item.dateTime || "-",
      cardUser: item.cardUser || "-",
      cardNumber: item.cardNumber || "-",
      place: item.place || "-",
      receiptNumber: item.receiptNumber || "-",
      Name: item.Name || "-",
      quantity: item.quantity,
      amount:
        (item.amountWithoutDiscount || 0) -
        (item.discountAmount || 0) -
        (item.receiptFees || 0) +
        (item.discountsOnTaxes || 0),
    })
  );

  const additionalExpensesColumns = [
    { key: "#" },
    { key: "cardUser", name: t("card_user") },
    { key: "cardNumber", name: t("card_no") },
    { key: "dateTime", name: t("date") },
    { key: "amount", name: t("amount"), type: "currency" },
  ] as Column[];

  const handleAddManualExpense = () => {
    // Reset form and show it
    setFormDate("");
    setFormExpenseType("fuel");
    setFormAmount("");
    setFormQuantity("");
    setFormDescription("");
    setFormFile(undefined);
    setFormMeasurementUnit("");
    setEditingExpenseId("new");
    // Clear any previous error or success messages
    setErrMsg("");
    setSuccessMessage("");
  };

  const handleEditExpense = (expense: ManualExpenseFormRow) => {
    // Populate form with expense data
    setFormDate(expense.date);
    setFormExpenseType(expense.expenseType);
    setFormAmount(expense.amount);
    setFormQuantity(expense.quantity);
    setFormDescription(expense.description);
    // Handle file as object or string ID
    setFormFile(expense.file);
    setFormMeasurementUnit(expense.measurementUnit || "");
    setEditingExpenseId(expense.id);
  };

  const handleDeleteExpense = (expenseId: string) => {
    setManualExpenseFormRows(
      manualExpenseFormRows.filter((row) => row.id !== expenseId)
    );
  };

  const handleCancelForm = () => {
    setEditingExpenseId(null);
    setFormDate("");
    setFormExpenseType("fuel");
    setFormAmount("");
    setFormQuantity("");
    setFormDescription("");
    setFormFile(undefined);
    setFormMeasurementUnit("");
    // Clear any error or success messages
    setErrMsg("");
    setSuccessMessage("");
  };

  const handleSaveExpense = () => {
    // Validation
    if (!formDate || !formAmount) {
      setErrMsg(t("field_is_required"));
      return;
    }
    if (isNaN(Number(formAmount)) || Number(formAmount) <= 0) {
      setErrMsg(t("must_be_number"));
      return;
    }
    if (formExpenseType === "fuel" && (!formQuantity || isNaN(Number(formQuantity)) || Number(formQuantity) <= 0)) {
      setErrMsg(t("field_is_required"));
      return;
    }

    if (editingExpenseId === "new") {
      // Add new expense
      const timestamp = new Date().toISOString();
      const newRowId = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newRow: ManualExpenseFormRow = {
        id: newRowId,
        date: formDate,
        expenseType: formExpenseType,
        amount: formAmount,
        quantity: formQuantity,
        description: formDescription,
        file: formFile,
        measurementUnit: formMeasurementUnit || undefined,
        createdAt: timestamp,
      };
      setManualExpenseFormRows([...manualExpenseFormRows, newRow]);
    } else {
      // Update existing expense
      setManualExpenseFormRows(
        manualExpenseFormRows.map((row) =>
          row.id === editingExpenseId
            ? {
                ...row,
                date: formDate,
                expenseType: formExpenseType,
                amount: formAmount,
                quantity: formQuantity,
                description: formDescription,
                // Preserve existing file if no new file was uploaded
                // formFile can be string (new upload) or object (existing), row.file can be object or string
                file: formFile !== undefined ? formFile : row.file,
                measurementUnit: formMeasurementUnit || undefined,
              }
            : row
        )
      );
    }
    
    // Clear form and hide it
    handleCancelForm();
    setSuccessMessage("saved_successfully");
    // Clear success message after 1 second
    setTimeout(() => {
      setSuccessMessage("");
    }, 1000);
  };

  const handleFormFileUpload = useCallback(
    async (acceptedFiles: File[]) => {
      if (!companyId || acceptedFiles.length === 0) return;
      setFormFileUploading(true);
      try {
        const file = acceptedFiles[0];
        const { data } = await upload(file, companyId);
        // Upload returns file entity with id, name, title - store as object to match state type
        if (typeof data === "object" && data?.id) {
          setFormFile({
            id: data.id,
            name: data.name,
            title: data.title,
          });
        } else if (typeof data === "string") {
          setFormFile(data);
        } else {
          setFormFile(undefined);
        }
      } catch (error) {
        console.error("Error uploading file:", error);
      } finally {
        setFormFileUploading(false);
        setFormFileDialogOpen(false);
      }
    },
    [companyId]
  );

  const toggleFormFileDialog = useCallback(() => {
    setFormFileDialogOpen((prev) => !prev);
  }, []);

  // Process expenses for table display
  const processedManualExpenses = manualExpenseFormRows.map(
    (row: ManualExpenseFormRow, idx: number) => {
      // Extract file ID and name/title for display
      const fileId = typeof row.file === "object" && row.file?.id 
        ? row.file.id 
        : typeof row.file === "string" 
          ? row.file 
          : null;
      const fileName = typeof row.file === "object" 
        ? (row.file?.name || row.file?.title || "-")
        : "-";
      
      // Format file as HTML link if exists
      const fileHtml = fileId 
        ? `<a href="${API_URL}/${companyId}/files/download/${fileId}" target="_blank" style="color: #0066cc; text-decoration: underline;">${escapeHtml(fileName) || "-"}</a>`
        : "-";
      
      return {
        id: row.id,
        "#": idx + 1,
        date: row.date ? formatPrettyDate(row.date, "lt-LT", false) : "-",
        expenseType: row.expenseType === "fuel" ? t("expense_type_fuel") : t("expense_type_other"),
        amount: row.amount ? parseFloat(row.amount) : 0,
        quantity: row.expenseType === "fuel" ? (row.quantity || "-") : "-",
        measurementUnit: row.expenseType === "fuel" ? (row.measurementUnit || "-") : "-",
        description: row.description || "-",
        file: fileHtml,
      };
    }
  );

  const manualExpensesColumns = [
    {
      key: "edit",
      name: t("edit"),
      type: "actionBtn",
      onClick: (item: any) => {
        const expense = manualExpenseFormRows.find((e) => e.id === item.id);
        if (expense) handleEditExpense(expense);
      },
      Icon: EditIcon,
    },
    {
      key: "delete",
      name: t("delete"),
      type: "actionBtn",
      onClick: (item: any) => handleDeleteExpense(item.id),
      Icon: DeleteIcon,
    },
    { key: "date", name: t("date") },
    { key: "expenseType", name: t("expense_type") },
    { key: "amount", name: t("amount"), type: "currency" },
    { key: "quantity", name: t("quantity") },
    { key: "measurementUnit", name: t("measurement_unit") },
    { 
      key: "file", 
      name: t("file"),
      type: "html_node",
    },
    { key: "description", name: t("description") },
  ] as Column[];


  return (
    <Form onSubmit={() => ""}>
      {isInitialLoading ? (
        <FuelReportFormSkeleton />
      ) : (
        <>
          <FormLayout>
            <div className="form-warnings">
              {errMsg && (
                <div className="">
                  <Banner tone="critical">{errMsg}</Banner>
                </div>
              )}
              {successMessage && (
                <div className="">
                  <Banner>{t(successMessage)}</Banner>
                </div>
              )}
            </div>
            <YearMonthSelect value={yearMonth} onChange={handleYearMonthChange} />
            <AssetsSelect
              value={selectedAsset}
              onChange={handleAssetChange}
              onLoaded={handleAssetsLoaded}
              isRequired={true}
              label={t("asset_select_car")}
              fetchAll={source !== "profile"}
            />
            <FuelCardSelect
              value={selectedFuelCard}
              onChange={handleFuelCardChange}
              isRequired={true}
              fetchAll={source !== "profile"}
            />
            {reportData && reportData.length > 0 && (
              <Card>
                <Text as="h3" variant="headingSm">
                  {t("fuel_records")}
                </Text>
                <div style={{ maxHeight: 220, overflowY: "auto", padding: 8 }}>
                  <DataTable
                    collection={processedReportData}
                    columns={[
                      { key: "cardUser", name: t("card_user") },
                      { key: "cardNumber", name: t("card_no") },
                      { key: "dateTime", name: t("date") },
                      { key: "quantity", name: t("quantity"), type: "number" },
                      { key: "amount", name: t("amount"), type: "currency" },
                    ]}
                  />
                </div>
              </Card>
            )}

            {additionalExpensesData && additionalExpensesData.length > 0 && (
              <Card>
                <Text as="h3" variant="headingSm">
                  {t("additional_expenses")}
                </Text>
                <div style={{ maxHeight: 220, overflowY: "auto", padding: 8 }}>
                  <DataTable
                    collection={processedAdditionalExpensesData}
                    columns={additionalExpensesColumns}
                  />
                </div>
              </Card>
            )}

            {/* Manual Expenses Section */}
            <Card>
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h3" variant="headingSm">
                  {t("manual_expenses")}
                </Text>
                {!editingExpenseId && (
                  <Button size="slim" onClick={handleAddManualExpense}>
                    {t("add_manual_expense")}
                  </Button>
                )}
              </InlineStack>

              {/* Form for Adding/Editing Expense */}
              {editingExpenseId && (
                <div style={{ marginTop: "16px", padding: "16px", backgroundColor: "#f6f6f7", borderRadius: "8px" }}>
                  <BlockStack gap="300">
                    <Text as="h4" variant="headingSm">
                      {editingExpenseId === "new" ? t("add_manual_expense") : t("edit_manual_expense")}
                    </Text>
                
                    {/* Row 1: Expense Type and Date */}
                    <InlineStack gap="300" align="start">
                      <div style={{ flex: 1 }}>
                        <Select
                          label={t("expense_type")}
                          options={[
                            { label: t("expense_type_fuel"), value: "fuel" },
                            { label: t("expense_type_other"), value: "other" },
                          ]}
                          value={formExpenseType}
                          onChange={(val) => setFormExpenseType(val as "fuel" | "other")}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <DatePicker
                          title={t("date")}
                          onChange={setFormDate}
                          selected={formDate}
                          isRequired={true}
                          disableDatesAfter=""
                        />
                      </div>
                    </InlineStack>

                    {/* Row 2: Amount */}
                    <InlineStack gap="300" align="start">
                      <div style={{ flex: 1 }}>
                        <TextField
                          label={t("amount")}
                          value={formAmount}
                          onChange={(val) => setTypedDecimal(setFormAmount, val)}
                          inputMode="decimal"
                          autoComplete="off"
                          requiredIndicator={true}
                        />
                      </div>
                    </InlineStack>

                    {/* Row 3: Quantity and Measurement Unit (only for fuel) */}
                    {formExpenseType === "fuel" && (
                      <InlineStack gap="300" align="start">
                        <div style={{ flex: 1 }}>
                          <TextField
                            label={t("quantity")}
                            value={formQuantity}
                            onChange={(val) => setTypedDecimal(setFormQuantity, val)}
                            inputMode="decimal"
                            autoComplete="off"
                            requiredIndicator={true}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <TextField
                            label={t("measurement_unit")}
                            value={formMeasurementUnit}
                            onChange={setFormMeasurementUnit}
                            autoComplete="off"
                          />
                        </div>
                      </InlineStack>
                    )}

                    {/* Row 4: Description */}
                    <TextField
                      label={t("description")}
                      value={formDescription}
                      onChange={setFormDescription}
                      autoComplete="off"
                    />

                    {/* Row 5: File Upload */}
                    <BlockStack gap="200">
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        {t("file")}
                      </Text>
                      <Button onClick={toggleFormFileDialog} loading={formFileUploading}>
                        {formFile ? t("replace_file") : t("upload_file")}
                      </Button>
                      {formFile && (
                        <Text as="p" variant="bodySm" tone="subdued">
                          {t("file_uploaded")}: {
                            typeof formFile === "object" 
                              ? (formFile.name || formFile.title || (formFile.id ? `File ID: ${formFile.id}` : "-"))
                              : formFile
                          }
                        </Text>
                      )}
                      <div style={{ display: "none" }}>
                        <DropZone
                          allowMultiple={false}
                          openFileDialog={formFileDialogOpen}
                          onDropAccepted={handleFormFileUpload}
                          onFileDialogClose={toggleFormFileDialog}
                          disabled={formFileUploading}
                        >
                          <DropZone.FileUpload />
                        </DropZone>
                      </div>
                    </BlockStack>

                    {/* Row 6: Save/Update and Cancel buttons */}
                    <InlineStack gap="200">
                      <Button onClick={handleSaveExpense} variant="primary">
                        {editingExpenseId === "new" ? t("save") : t("update")}
                      </Button>
                      <Button onClick={handleCancelForm} variant="secondary">
                        {t("cancel")}
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </div>
              )}

              {/* Table Display of Saved Expenses */}
              {manualExpenseFormRows.length > 0 && (
                <div style={{ marginTop: "16px" }}>
                  <div style={{ maxHeight: 400, overflowY: "auto", padding: 8 }}>
                    <DataTable
                      collection={processedManualExpenses}
                      columns={manualExpensesColumns}
                    />
                  </div>
                </div>
              )}

              {manualExpenseFormRows.length === 0 && !editingExpenseId && (
                <div style={{ marginTop: "16px", padding: "16px", textAlign: "center" }}>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    {t("no_manual_expenses_please_add")}
                  </Text>
                </div>
              )}
            </Card>

            <TextField
              label={t("start_of_month_km")}
              inputMode="decimal"
              value={startOfMonthKm}
              onChange={(val) => {
                if (!startKmLocked) setTypedDecimal(setStartOfMonthKm, val);
              }}
              error={errors.startOfMonthKm}
              autoComplete="off"
              disabled={startKmLocked}
              requiredIndicator={true}
            />
            <TextField
              label={t("end_of_month_km")}
              inputMode="decimal"
              value={endOfMonthKm}
              onChange={(val) => setTypedDecimal(setEndOfMonthKm, val)}
              error={errors.endOfMonthKm}
              autoComplete="off"
              requiredIndicator={true}
            />

            {!isElectricVehicle && (
              // One FormLayout item holds both fields, so the gap between them is set here.
              <BlockStack gap="400">
                <TextField
                  label={t("start_of_month_ltr")}
                  inputMode="decimal"
                  value={startOfMonthLtr}
                  onChange={(val) => {
                    if (!startLtrLocked) setTypedDecimal(setStartOfMonthLtr, val);
                  }}
                  error={errors.startOfMonthLtr}
                  autoComplete="off"
                  disabled={startLtrLocked}
                  requiredIndicator={true}
                />

                <TextField
                  label={t("end_of_month_ltr")}
                  inputMode="decimal"
                  value={endOfMonthLtr}
                  onChange={(val) => setTypedDecimal(setEndOfMonthLtr, val)}
                  error={errors.endOfMonthLtr}
                  autoComplete="off"
                  requiredIndicator={true}
                />
              </BlockStack>
            )}

            <TextField
              label={t("ev_charge_kw")}
              inputMode="decimal"
              value={evChargeKw}
              onChange={(val) => setTypedDecimal(setEvChargeKw, val)}
              error={errors.evChargeKw}
              autoComplete="off"
            />

            <TextField
              label={t("amount")}
              inputMode="decimal"
              value={evChargeAmount}
              onChange={(val) => setTypedDecimal(setEvChargeAmount, val, 5)}
              error={errors.evChargeAmount}
              autoComplete="off"
            />

            {isElectricVehicle && (
              <BlockStack gap="400">
                <TextField
                  label={t("work_purpose_km")}
                  inputMode="decimal"
                  value={workPurposeKm}
                  onChange={(val) => {
                    const decimal = typedDecimal(val);
                    if (decimal !== null) {
                      setWorkPurposeKmTouched(true);
                      setWorkPurposeKm(decimal);
                    }
                  }}
                  error={errors.workPurposeKm}
                  autoComplete="off"
                  requiredIndicator={true}
                  helpText={t("work_purpose_km_from_rate")}
                />
                <TextField
                  label={t("non_work_purpose_km")}
                  type="text"
                  value={
                    startOfMonthKm && endOfMonthKm ? String(nonWorkPurposeKm) : ""
                  }
                  autoComplete="off"
                  disabled
                />
              </BlockStack>
            )}
          </FormLayout>
          {/* On a phone the form is long: the save button stays at the bottom of the sheet. */}
          <div className="mt-4 max-md:sticky max-md:bottom-0 max-md:z-10 max-md:bg-white max-md:py-3">
            <Button
              onClick={handleFormSubmit}
              loading={isLoading}
              disabled={isLoading || isSaved}
              size={isPhone ? "large" : "medium"}
              fullWidth={isPhone}
            >
              {t(isEditMode ? "update" : "save")}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};

/**
 * A saved form only closes the modal, and the modal keeps a closing form on screen for a
 * moment, so a form opened right after it meets the same instance: the old month, the old
 * vehicle, «saved» still on. The key gives a form opened for another report, month or
 * vehicle a fresh start with its own empty fields and its own list of vehicles.
 */
const FuelReportForm: React.FC<AddFuelReportFormProps> = (props) => (
  <AddFuelReportForm
    key={`${props.id}|${props.period}|${props.assetId}`}
    {...props}
  />
);

export default FuelReportForm;
